import { getLogger, getThemeColor, makeStyles } from "@mcleod/core";
import { getImageModule } from "@mcleod/images";
import { Component } from "../../base/Component";
import { ComponentPropDefinitions } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { ImagePropDefinitions, ImageProps } from "./ImageProps";
import { ImageType } from "./ImageType";

const log = getLogger("components.Image");

const classes = makeStyles("img", () => {
  return {
    imageBase: { fill: "currentcolor", height: "1.5em", width: "1.5em", userSelect: "none" },
    rotate: { animation: "rotation 4s infinite linear" },
  }
});

export class Image extends Component implements ImageProps {
  private _fill: string;
  private _imageBytes: string;
  private _imageType: ImageType;
  private _name: string;
  private _rotation: number;
  private _stroke: string;
  private _strokeWidth: number;
  private _rotate: boolean;
  private _busy: boolean;
  private _nameBeforeBusy: string;

  constructor(props?: Partial<ImageProps>) {
    if (props != null && (props.imageType === ImageType.IMG)) {
      super("img", props);
      this._imageType = props.imageType;
      delete props.imageType;
    }
    else if (props != null && (props.imageType === ImageType.PDF)) {
      super("object", props);
      this._imageType = props.imageType;
      delete props.imageType;
    }
    else {
      super("div");
      (this as any)._element = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      this._initElement(props);
    }
    this._element.setAttribute("class", classes.imageBase);
    this.setProps(props);
  }

  setProps(props: Partial<ImageProps>) {
    super.setProps(props);
  }

  get name():string {
    return this._name;
  }

  set name(value:string) {
    if (this._name === value)
      return;
    this._name = value;
    if (this._nameBeforeBusy != null && !this.busy)
      this._nameBeforeBusy = value;
    if (value == null || value.length === 0) {
      this._element.innerHTML = "";
      return;
    }
    const module = getImageModule(value);
    if (module == null) {
      log.info("Could not find module for image " + value);
      return;
    }
    const props = (module as any).props;
    if (props != null)
      for (const key in props) {
        if (key === "fillRule")
          this._element.setAttribute("fill-rule", props[key]);
        else if (key !== "innerHTML")
          this._element.setAttribute(key, props[key]);
      }
    if (props == null || props.viewBox == null)
      this._element.setAttribute("viewBox", "0 0 24 24");
    if ((module as any).innerHTML != null)
      this._element.innerHTML = (module as any).innerHTML;
    else {
      this._element.innerHTML = "";
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", module.path);
      this._element.appendChild(path);
    }
  }

  set rotation(degrees:number) {
    this._rotation = degrees;
    if (degrees == null)
      this._element.style.transform = "";
    else
      this._element.style.transform = "rotate(" + degrees + "deg)";
  }

  get rotation():number {
    return this._rotation;
  }

  get stroke():string {
    return this._stroke;
  }

  set stroke(value:string) {
    this._stroke = value;
    this._element.style.stroke = getThemeColor(value);
  }

  get fill():string {
    return this._fill;
  }

  set fill(value:string) {
    this._fill = value;
    this._element.style.fill = getThemeColor(value);
  }

  get strokeWidth():number {
    return this._strokeWidth;
  }

  set strokeWidth(value:number) {
    this._strokeWidth = value;
    if (value == null)
      this._element.style.strokeWidth = "";
    else
      this._element.style.strokeWidth = value.toString();
  }

  set preserveAspectRatio(value:boolean) {
    if (value)
      this._element.style.objectFit = "contain";
    else
      this._element.style.objectFit = "";
  }

  get imageBytes():string {
    return this._imageBytes;
  }

  set imageBytes(value:string) {
    this._imageBytes = value;
    if (this.imageType === ImageType.IMG) {
      if (!value.startsWith("data:image"))
        value = "data:image/jpeg;base64," + value;
      this._element.setAttribute("src", value);
    }
    else if (this.imageType === ImageType.PDF) {
      const base64 = window.atob(value);
      const len = new Array(base64.length);
      for (let i = 0; i < base64.length; i++)
        len[i] = base64.charCodeAt(i);
      const byteArray = new Uint8Array(len);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      let fileURL = URL.createObjectURL(blob);
      fileURL += "#toolbar=0&navpanes=0";
      this._element.setAttribute("data", fileURL);
    }
    else
      throw new Error("Cannot set imageBytes on an SVG image.");
  }

  get imageType():ImageType {
    return this._imageType
  }

  public override getPropertyDefinitions(): ComponentPropDefinitions {
    return ImagePropDefinitions.getDefinitions();
  }

  get rotate() {
    return this._rotate == null ? false : this._rotate;
  }

  set rotate(value: boolean) {
    this._rotate = value;
    if (value)
      this._element.classList.add(classes.rotate);
    else
      this._element.classList.remove(classes.rotate);
  }

  get busy(): boolean {
    return this._busy == null ? false : this._busy;
  }

  set busy(value: boolean) {
    this._busy = value;
    if (value === true) {
      this._nameBeforeBusy = this._name;
      this.name = "spinner";
    }
    else  {
      this.name = this._nameBeforeBusy;
      delete this._nameBeforeBusy;
    }
    this.rotate = value;
  }

  override get serializationName() {
    return "image";
  }

  override getBasicValue(): any {
    return this.name;
  }
}

ComponentTypes.registerComponentType("image", Image.prototype.constructor);
